// Контрол термических фронтов: тумблер + ползунок силы + переключатель единицы
// градиента (°C/100 км ↔ °C/10 км). Независим от изотермы — можно включить оба
// слоя. Смена силы/единицы пересчитывает фронты в браузере по уже скачанной
// сетке (без сети). Единица /10 км достаёт до очень резких фронтов.
import { formatNumber, useLang, useT } from '../i18n'
import { frontUnitLabel } from './legendRows'
import { FRONT_STRENGTH_STEP, type FrontUnit } from './frontsMath'
import { OverlaySection } from './OverlaySection'
import { frontSwatch } from './overlaySwatch'

interface Props {
  enabled: boolean
  value: number
  unit: FrontUnit
  min: number
  max: number
  onToggle: (on: boolean) => void
  onChange: (value: number) => void
  onUnitChange: (unit: FrontUnit) => void
}

const UNITS: FrontUnit[] = ['100km', '10km']

export function FrontsControl({
  enabled,
  value,
  unit,
  min,
  max,
  onToggle,
  onChange,
  onUnitChange,
}: Props) {
  const t = useT()
  const { lang } = useLang()
  const fmt = (v: number) => formatNumber(lang, v, 1)
  return (
    <OverlaySection
      title={t('maps.fronts.title')}
      hint="fronts"
      swatch={frontSwatch()}
      enabled={enabled}
      toggleLabel={t('maps.fronts.toggle')}
      onToggle={onToggle}
    >
      {/* Переключатель единицы градиента: /10 км — для очень резких фронтов */}
      <div
        role="radiogroup"
        aria-label={t('maps.fronts.unitAria')}
        className="mb-2.5 flex gap-1 rounded-md border border-hull bg-abyss/60 p-0.5"
      >
        {UNITS.map(u => (
          <button
            key={u}
            type="button"
            role="radio"
            aria-checked={unit === u}
            onClick={() => onUnitChange(u)}
            className={
              'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ' +
              (unit === u ? 'bg-sonar text-abyss' : 'text-mist hover:text-foam')
            }
          >
            {frontUnitLabel(u)}
          </button>
        ))}
      </div>

      <div className="mb-0.5 flex items-baseline gap-1">
        <span className="tnum text-2xl font-bold leading-none text-foam">{fmt(value)}</span>
        <span className="text-xs font-medium text-sonar">{frontUnitLabel(unit)}</span>
      </div>
      <div className="mb-2 text-[11px] text-mist">{t('maps.fronts.strength')}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={FRONT_STRENGTH_STEP}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        aria-label={t('maps.fronts.strengthAria')}
        className="isotherm-range w-full"
      />
      <div className="mt-1 flex justify-between tnum text-[10px] text-mist">
        <span>{t('maps.fronts.weaker')}</span>
        <span>{t('maps.fronts.sharper')}</span>
      </div>
    </OverlaySection>
  )
}
